/**
 * Partner platform types (partners.tabbio.com).
 *
 * Distinct from `partners.ts`, which covers the anonymous marketing intake form
 * on tabbio.com/partners. This file describes the account a Tabbio user creates
 * for themselves on the portal.
 *
 * The const array is the single source of truth for the allowed statuses. It is
 * shared across the boundary deliberately: apps/web and apps/partners are on
 * zod ^4 and apps/api is on zod ^3, so the Zod schemas are duplicated on each
 * side, but every one of them builds its `z.enum(...)` from this array.
 *
 * There is no `pending`: joining the programme is self-serve, so an account is
 * active from the moment it exists. `suspended` is an admin decision against a
 * live account. `closed` is terminal: set by account deletion when the
 * partner's Tabbio user is erased, with the display fields redacted. The row
 * stays only because the commission and payout ledger key on its id; every
 * `status === 'active'` gate (links, attribution, accrual, payouts, KYC, the
 * studio) refuses it, and the admin console can neither suspend nor reinstate
 * it.
 */

export const PARTNER_ACCOUNT_STATUSES = ['active', 'suspended', 'closed'] as const;

export type PartnerAccountStatus = (typeof PARTNER_ACCOUNT_STATUSES)[number];

/** Partner-facing account shape. Payout details stay server side. */
export interface PartnerAccountSummary {
  id: string;
  status: PartnerAccountStatus;
  displayName: string;
  /**
   * One line about who the partner helps, shown under their name on the card a
   * shared link unfurls into. Null when they have not written one, which is the
   * same state as having cleared it: the card falls back to its generic line for
   * both.
   */
  publicDescription: string | null;
  commissionRateBps: number;
  /** ISO 8601. When the partner joined the programme. */
  appliedAt: string;
  /**
   * ISO 8601. When the account became active, which for a self-created account
   * is the moment it was created. Nullable only because rows written before
   * self-serve joining could sit un-activated.
   */
  approvedAt: string | null;
}

/**
 * Where a referral link sends the visitor.
 *
 * `signup` goes to the app's signup screen. `skill` goes to one of the partner's
 * own Skills Hub skills, which is the sharing case the partner program was asked
 * for. The portal redirect resolves the code and owns the destination URLs; the
 * API only reports which kind it is.
 */
export const REFERRAL_CODE_DESTINATIONS = ['signup', 'skill'] as const;

export type ReferralCodeDestination =
  (typeof REFERRAL_CODE_DESTINATIONS)[number];

/**
 * Archived is a retirement, not a deletion: referrals already captured through a
 * code keep pointing at it, so the row stays and only stops resolving.
 */
export const REFERRAL_CODE_STATUSES = ['active', 'archived'] as const;

export type ReferralCodeStatus = (typeof REFERRAL_CODE_STATUSES)[number];

/**
 * Stored commission state. Deliberately only two values.
 *
 * Whether a pending entry is still held or has already become payable is derived
 * from `payableAt` against the current time, never stored, so nothing has to keep
 * a column truthful as the 30-day hold elapses.
 */
export const PARTNER_COMMISSION_STATUSES = ['pending', 'paid'] as const;

export type PartnerCommissionStatus =
  (typeof PARTNER_COMMISSION_STATUSES)[number];

/**
 * The buckets a partner actually reads, derived server side from the stored
 * status and `payableAt` against one consistent clock per request.
 *
 * `pending` is inside the refund hold window, `payable` has cleared it and is
 * waiting for a payout, `paid` has been paid out. Sums of each bucket are net of
 * negative reversal entries.
 */
export const PARTNER_COMMISSION_BUCKETS = [
  'pending',
  'payable',
  'paid',
] as const;

export type PartnerCommissionBucket =
  (typeof PARTNER_COMMISSION_BUCKETS)[number];

/**
 * Where the amount the customer paid came from.
 *
 * `event_price` is the charge the payment provider reported. `catalog` is the
 * plan's list price, used when the provider event carried no usable amount. The
 * two can legitimately disagree (promotions, store price changes, currency), so
 * every entry records which one it was priced from.
 */
export const PARTNER_COMMISSION_BASE_AMOUNT_SOURCES = [
  'event_price',
  'catalog',
] as const;

export type PartnerCommissionBaseAmountSource =
  (typeof PARTNER_COMMISSION_BASE_AMOUNT_SOURCES)[number];

/**
 * One commission ledger entry, as the portal lists it.
 *
 * Amounts are integer minor units (fils, cents) of `currency`; there is no float
 * anywhere in the pipeline and amounts of different currencies are never added.
 * `amountMinor` is negative on a reversal entry, which is how a refund is
 * represented: the original entry stays exactly as it was and a compensating
 * row takes the money back.
 */
export interface PartnerCommissionEntry {
  id: string;
  /** The commission itself. Negative on a reversal entry. */
  amountMinor: number;
  /**
   * The agency member whose referral earned this entry, when it was rolled up to
   * an agency owner. Null means the entry was earned directly by the account it
   * accrued to, which is the case for every partner outside an agency and for an
   * agency owner's own referrals.
   */
  earnedBy: PartnerCommissionEarner | null;
  /**
   * True when this entry accrued to an agency owner rather than to the partner who
   * earned it. Caller-independent: the same row reads the same way for the owner
   * (whose balance it is in) and for the member (who earned it but does not hold
   * the balance).
   */
  accruesToAgency: boolean;
  /** ISO 4217, uppercase. */
  currency: string;
  status: PartnerCommissionStatus;
  /** Derived, not stored. See PARTNER_COMMISSION_BUCKETS. */
  bucket: PartnerCommissionBucket;
  /** The payment provider event type this entry came from, verbatim. */
  eventType: string;
  /** Plan snapshot: `plus`, `pro`, or `unknown` when the product did not map. */
  plan: string;
  /** `monthly` or `yearly`, null when the product did not resolve to one. */
  billingCycle: string | null;
  /** What the customer paid, in minor units. Never negative. */
  baseAmountMinor: number;
  baseAmountSource: PartnerCommissionBaseAmountSource;
  /** The partner's share in basis points, snapshotted at accrual time. */
  rateBps: number;
  /** ISO 8601. When the payment happened, from the provider event. */
  occurredAt: string;
  /** ISO 8601. When the entry stops being held and becomes payable. */
  payableAt: string;
  /** The entry this row takes back, set only on reversals. */
  reversalOfId: string | null;
  /** Provider transaction id from RevenueCat. */
  transactionId?: string | null;
  /** Provider original transaction id from RevenueCat. */
  originalTransactionId?: string | null;
  /**
   * The payout that has claimed this entry, null while the money is unclaimed.
   *
   * A stamped entry keeps `status: 'pending'` until its payout is actually paid,
   * but it has already left the payable balance: it counts in `requestedMinor`
   * instead, because the money is spoken for and must not be requested twice.
   */
  payoutId: string | null;
  /**
   * Who paid, as "First L." (D65): a first name and last initial, never an
   * email or a full name. Null when the customer has no name on file, which the
   * portal shows as a generic "Customer".
   */
  customerLabel?: string | null;
  /**
   * Set on a payment entry that a refund has taken back, null otherwise. The
   * amount is what the reversal took (always positive here), so a partial
   * refund reads as partial.
   */
  refund?: PartnerCommissionRefund | null;
  /** Set on a reversal or a refund restore: the entry it points at. */
  reversalOf?: PartnerCommissionReversalTarget | null;
}

/** A refund that took back (part of) a payment entry. */
export interface PartnerCommissionRefund {
  /** ISO 8601. When the refund happened. */
  reversedAt: string;
  /** What the refund took back, as a positive magnitude. */
  amountMinor: number;
}

/** The entry a reversal (or a refund restore) points at, as the portal names it. */
export interface PartnerCommissionReversalTarget {
  /** ISO 8601. When that entry's payment (or refund) happened. */
  occurredAt: string;
  amountMinor: number;
  plan: string;
}

/**
 * Which slice of the ledger an earnings page shows. `earnings` is payments,
 * `refunds` is reversals and refund restores.
 */
export const PARTNER_EARNINGS_KINDS = ['all', 'earnings', 'refunds'] as const;

export type PartnerEarningsKind = (typeof PARTNER_EARNINGS_KINDS)[number];

/**
 * Where a referred customer's subscription stands, as a partner sees it (D65).
 *
 * - `trial`: on a free trial; no commission until the first paid period.
 * - `active`: paid and renewing; commission continues each billing cycle.
 * - `ending`: cancelled but still inside a paid period. The current cycle was
 *   the last commission; it ends on `statusDate`.
 * - `ended`: the subscription has run out after paying.
 * - `refunded`: the latest payment was refunded and there is no access.
 * - `free`: signed up through the partner and has never paid.
 */
export const PARTNER_SUBSCRIBER_STATUSES = [
  'trial',
  'active',
  'ending',
  'ended',
  'refunded',
  'free',
] as const;

export type PartnerSubscriberStatus =
  (typeof PARTNER_SUBSCRIBER_STATUSES)[number];

/** Net commission from one customer in one currency. */
export interface PartnerSubscriberEarning {
  currency: string;
  netMinor: number;
}

/** One referred customer in the partner's subscribers list. */
export interface PartnerSubscriber {
  /** The referral row id: stable, and never the customer's user id. */
  id: string;
  /** "First L.", or null when the customer has no name on file. */
  customerLabel: string | null;
  status: PartnerSubscriberStatus;
  /**
   * ISO 8601, meaning depends on `status`: renews on (active), ends on (ending,
   * trial), ended on (ended), refunded on (refunded). Null for `free` or when
   * the store reported no period end.
   */
  statusDate: string | null;
  /** `plus`, `pro`, or null when the customer is on no paid plan. */
  plan: string | null;
  billingCycle: string | null;
  /** True when the subscription is a company (team) plan the customer owns. */
  teamPlan: boolean;
  /** ISO 8601. When the customer signed up through the partner. */
  referredAt: string;
  /** ISO 8601. First and latest payment that earned commission. */
  firstPaidAt: string | null;
  lastPaidAt: string | null;
  /** Net of refunds, per currency. Empty when nothing was earned. */
  earnings: PartnerSubscriberEarning[];
  refundCount: number;
  /**
   * The agency member whose link brought this customer in, when it was not
   * the caller (an agency owner sees their members' customers that pay them).
   */
  referredByMember: string | null;
}

export type PartnerSubscriberSummary = Record<PartnerSubscriberStatus, number>;

export interface PartnerSubscribersPage {
  /** Counts across every subscriber, regardless of the status filter. */
  summary: PartnerSubscriberSummary;
  items: PartnerSubscriber[];
  /** Matching the status filter, not the size of this page. */
  total: number;
  page: number;
  pageSize: number;
}

/** The agency member named on a rolled-up commission entry. */
export interface PartnerCommissionEarner {
  partnerAccountId: string;
  /** The member's partner display name, null when their account has gone missing. */
  displayName: string | null;
}

/**
 * Bucket totals for one currency, net of reversals.
 *
 * One element per currency present in the partner's ledger. Currencies are never
 * combined: there is no exchange rate in this system, so a partner paid in two
 * currencies has two balances.
 *
 * The four buckets partition the ledger: every entry is counted in exactly one of
 * them, which is what makes `lifetimeMinor` a reconciliation identity rather than
 * a display total.
 */
export interface PartnerCommissionBalance {
  /** ISO 4217, uppercase. */
  currency: string;
  /** Still inside the refund hold window. */
  pendingMinor: number;
  /** Cleared the hold window and free to be requested. */
  payableMinor: number;
  /**
   * Cleared, and already claimed by an open payout.
   *
   * Deliberately not part of `payableMinor`: the money is spoken for by a payout
   * that has not been paid yet, so it must not be requested a second time. A
   * rejected payout releases its entries and they return to `payableMinor`.
   */
  requestedMinor: number;
  paidMinor: number;
  /** pending + payable + requested + paid, net of reversals. */
  lifetimeMinor: number;
}

/** One page of a partner's earnings, with their balances alongside it. */
export interface PartnerEarningsPage {
  balances: PartnerCommissionBalance[];
  items: PartnerCommissionEntry[];
  /** Total entries the partner has, not the size of this page. */
  total: number;
  page: number;
  pageSize: number;
}

/**
 * A partner's client record.
 *
 * Archived is a retirement, not a deletion: the CVs a partner already built for
 * a client keep pointing at the record, so it stays and only leaves the working
 * list.
 */
export const PARTNER_CLIENT_STATUSES = ['active', 'archived'] as const;

export type PartnerClientStatus = (typeof PARTNER_CLIENT_STATUSES)[number];

/**
 * The state of one CV in a Client Portfolio Health Check (partner lead magnet
 * idea 1). `pending` holds a reserved slot while the upload is still being
 * parsed (no content stored yet). `ready` is parsed and scored, `unreadable` had too little text to
 * score, `failed` could not be parsed at all (the parser or its provider
 * refused). The import moves `ready` to `importing` (claimed by exactly one
 * request) and then to `imported`, when the client CV exists and the parse is
 * deleted.
 */
export const PARTNER_PORTFOLIO_ITEM_STATUSES = [
  'pending',
  'ready',
  'unreadable',
  'failed',
  'importing',
  'imported',
] as const;

export type PartnerPortfolioItemStatus =
  (typeof PARTNER_PORTFOLIO_ITEM_STATUSES)[number];

/**
 * One client in the partner CV studio.
 *
 * A client is not a Tabbio user and never needs to become one: these fields are
 * the partner's own record of who they are building CVs for. `notes` is private
 * to the partner and never rendered on a CV.
 */
export interface PartnerClientSummary {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  notes: string | null;
  status: PartnerClientStatus;
  /** How many CVs the partner has built for this client. */
  cvCount: number;
  /**
   * The CV to open for this client: the most recently edited one, or null when
   * there is none yet. Additive, so a caller that ignores it is unaffected; the
   * partner portal's client list uses it to reach the editor in one click
   * instead of stopping at the client record first.
   */
  latestCvId: string | null;
  /** ISO 8601. */
  createdAt: string;
  /** ISO 8601, null while the client is active. */
  archivedAt: string | null;
}

/** One page of a partner's client list. */
export interface PartnerClientsPage {
  items: PartnerClientSummary[];
  /** Total clients matching the filter, not the size of this page. */
  total: number;
  page: number;
  pageSize: number;
}

/**
 * A CV built for a client, as the studio lists it.
 *
 * `id` is the link row, `cvId` the CV itself. Both are returned because the
 * studio addresses CVs by `cvId` while the link row carries the delivery state.
 */
export interface PartnerClientCvSummary {
  id: string;
  cvId: string;
  title: string;
  /** ISO 8601. When the CV content last changed. */
  updatedAt: string;
  /** ISO 8601, null until the CV has actually been handed to the client. */
  deliveredAt: string | null;
  /** Same meaning as on `PartnerCvListItem`. */
  transferred: boolean;
  /** Same meaning as on `PartnerCvListItem`. */
  reclaimedAt: string | null;
  /** Same meaning as on `PartnerCvListItem`. */
  handoffState: PartnerCvHandoffState | null;
  /** Same meaning as on `PartnerCvListItem`. */
  importScore: number | null;
  /** Same meaning as on `PartnerCvListItem`. */
  deliveredScore: number | null;
}

/**
 * What the CV Builder's list filters on.
 *
 * One select rather than two, because a partner asks one question of this list.
 * `all` is every CV of an ACTIVE client whatever its delivery state; `draft` and
 * `delivered` narrow that by delivery; `transferred` narrows to CVs whose
 * handoff the client claimed as a transfer, which are read-only history;
 * `archived` is the escape hatch that shows the work of clients who have been
 * retired, which is otherwise invisible now that the Builder lists documents
 * instead of people.
 */
export const PARTNER_CV_LIST_FILTERS = [
  'all',
  'draft',
  'delivered',
  'transferred',
  'archived',
] as const;

export type PartnerCvListFilter = (typeof PARTNER_CV_LIST_FILTERS)[number];

/**
 * How a partner hands a CV to their client.
 *
 * `copy` gives the client their own copy and the partner keeps a working
 * original. `transfer` also gives the client their own copy, but the partner's
 * original becomes read-only "Transferred" history the moment the client
 * claims it. The state is derived from the linked handoff row (status
 * `claimed` plus mode `transfer`), never stored on the CV itself.
 */
export const PARTNER_CV_HANDOFF_MODES = ['copy', 'transfer'] as const;

export type PartnerCvHandoffMode = (typeof PARTNER_CV_HANDOFF_MODES)[number];

/**
 * Where the invite a partner sent has got to, as the studio reports it.
 *
 * Every one of these is DERIVED from the linked `CVHandoff` row on read - the
 * CV row and the link row are never mutated by a claim (landmine 52 territory),
 * so the handoff is the only truth about what the client did.
 *
 * `pending` is sent-and-still-actionable, `expired` is a pending invite whose
 * `expiresAt` has passed (expiry is derived, never stored, so no job has to
 * keep a column truthful), `claimed` is the client having taken their copy, and
 * `cancelled` is the partner having withdrawn it. The `draft` handoff status
 * never appears here: a draft never left the building, and the link row is only
 * stamped once an invite has actually gone out.
 *
 * NOT the same axis as `transferred`: a transferred CV is `claimed` too. The
 * mode is what separates the two, and the studio shows the settled word.
 */
export const PARTNER_CV_HANDOFF_STATES = [
  'pending',
  'claimed',
  'expired',
  'cancelled',
] as const;

export type PartnerCvHandoffState =
  (typeof PARTNER_CV_HANDOFF_STATES)[number];

/**
 * One CV in the partner's CV Builder, flattened across every client.
 *
 * The Builder lists documents, not people: a partner opens it to work on a CV,
 * and a client with three CVs is three rows here rather than one row they have
 * to open first. Each row therefore carries enough of its client to be labelled
 * and searched by, without the caller fetching the client separately.
 *
 * `id` is the link row and `cvId` is the CV, exactly as in
 * `PartnerClientCvSummary`; the two are never interchangeable.
 */
export interface PartnerCvListItem {
  id: string;
  cvId: string;
  title: string;
  /** The client this CV was built for. Their record is one click from the row. */
  clientId: string;
  clientName: string;
  clientEmail: string | null;
  clientPhone: string | null;
  /** Archived clients' CVs stay editable; the row just says so. */
  clientStatus: PartnerClientStatus;
  /** ISO 8601. When the CV content last changed. */
  updatedAt: string;
  /** ISO 8601, null until the CV has actually been handed to the client. */
  deliveredAt: string | null;
  /**
   * True once the client claimed this CV's handoff in `transfer` mode. A
   * transferred CV is read-only history: edits, tailoring, re-sending, and
   * deletion are refused (PARTNER_CV_TRANSFERRED), while viewing, exporting,
   * and share links stay allowed.
   */
  transferred: boolean;
  /**
   * ISO 8601 of the partner taking this CV back out of read-only history
   * (D38), null for every CV that never was there.
   *
   * A reclaim never touches the handoff row: the client did claim the CV and
   * keeps the copy they claimed, so `handoffState` stays `claimed` and this
   * stamp is what makes `transferred` false again. That is also why it is
   * reported rather than folded into `transferred`: without it a reclaimed row
   * would be indistinguishable from a plain claimed `copy`, and the partner
   * would lose the trace of having taken their original back.
   */
  reclaimedAt: string | null;
  /**
   * How far the invite for this CV got, or null when none was ever linked.
   *
   * `deliveredAt` alone cannot answer this: it records that an email went out
   * and never changes again, so a partner reading it could not tell an invite
   * nobody opened from one their client already claimed. This is the field that
   * separates them, and it is why a row can say "Awaiting client" rather than
   * calling everything "Delivered" forever.
   */
  handoffState: PartnerCvHandoffState | null;
  /**
   * The deterministic partner CV Health score of the CV as it was imported
   * (LM2, D48), or null. Only ever set BESIDE a `deliveredScore` made under
   * the same score version, so a row that has one can say "up from" honestly;
   * a draft, a blank CV, or a non-comparable pair answers null.
   */
  importScore: number | null;
  /** The same score for the CV as it was last sent to the client, or null. */
  deliveredScore: number | null;
  /**
   * Whether the client who claimed this CV is paying, answered ONLY when
   * they are this partner's own referral: true or false then, null for a CV
   * not claimed yet and for a claimer the partner did not refer (whose
   * billing is not the partner's business).
   */
  recipientPaying: boolean | null;
}

/** One page of the CV Builder's flat CV list. */
export interface PartnerCvsPage {
  items: PartnerCvListItem[];
  /** Total CVs matching the filter, not the size of this page. */
  total: number;
  page: number;
  pageSize: number;
}

/** A view link a partner minted for a client CV. Read-only, download allowed. */
export interface PartnerCvShareLink {
  url: string;
  /** ISO 8601, null when the link does not expire. */
  expiresAt: string | null;
}

/** The delivery record created when a partner hands a CV to their client. */
export interface PartnerCvHandoff {
  id: string;
  status: string;
  mode: PartnerCvHandoffMode;
  /** ISO 8601, null when the invite does not expire. */
  expiresAt: string | null;
  /**
   * The claim link the invite email carries (`{portal}/c/{token}`), so the
   * studio can show it as a QR the partner holds up to their client's phone.
   *
   * Returned to the CREATOR only, and only on the response to their own send -
   * the same thing the consumer send endpoint has always handed back to the
   * person who minted the invite. It is never readable afterwards: the token is
   * stored hashed and this is the one moment its plaintext exists.
   *
   * Null on a cancel, which mints nothing.
   */
  inviteUrl: string | null;
}

/** One of a partner's referral links, as the portal lists it. */
export interface PartnerReferralCodeSummary {
  id: string;
  /**
   * The public code. Generated and opaque for every link except the partner's
   * main one, whose code is derived from their display name because it is the
   * link they read out loud and print.
   */
  code: string;
  /** Partner's own label for their list. Never shown to a visitor. */
  label: string | null;
  destination: ReferralCodeDestination;
  /** `Skill.skillId` slug when destination is `skill`, otherwise null. */
  skillId: string | null;
  status: ReferralCodeStatus;
  /**
   * The partner's main name-code. At most one active link carries this. A
   * renamed main link keeps working: the old row stays active and only loses
   * the flag, so printed links never break.
   */
  isPrimary: boolean;
  clickCount: number;
  /**
   * CV Health reviews started on this link's CV score page (LM1). A counter,
   * so it outlives the 30-day report rows it counts.
   */
  cvCheckCount: number;
  /** Signups attributed to this code. */
  signupCount: number;
  /**
   * Signups through this code that have paid: the referred user's commission
   * entries net above zero in at least one currency, so a fully refunded
   * customer stops counting. Currencies are never summed together.
   */
  payingCount: number;
  /** ISO 8601. */
  createdAt: string;
  /** ISO 8601, null while the code is active. */
  archivedAt: string | null;
}

/**
 * Lifetime totals across the partner's ACTIVE links only.
 *
 * Archived links are excluded deliberately: these figures sit next to the list
 * of links a partner can still share, so counting retired ones would describe a
 * different set than the screen shows. `paying` counts distinct customers, and
 * one referred user belongs to exactly one link, so it never double counts.
 */
export interface PartnerReferralCodeTotals {
  clicks: number;
  signups: number;
  paying: number;
  /** CV score page reviews across the active codes. */
  cvChecks: number;
}

/** `GET /api/partner/referral-codes`: every link plus the active-link totals. */
export interface PartnerReferralCodesPage {
  items: PartnerReferralCodeSummary[];
  totals: PartnerReferralCodeTotals;
  /**
   * Whether signup links open the partner CV score page right now (both CV
   * Health launch flags on), the same answer `resolve` gives (LM1).
   */
  cvCheck: boolean;
}

/**
 * One month of commission in one currency, net of reversals.
 *
 * `month` is `YYYY-MM` in UTC. Months and currencies with no entries are absent
 * rather than zero, so a chart fills its own gaps and never invents a currency
 * the partner has not earned in.
 */
export interface PartnerCommissionMonthPoint {
  /** `YYYY-MM`, UTC. */
  month: string;
  /** ISO 4217, uppercase. */
  currency: string;
  /** Net minor units for this month, reversals included. */
  amountMinor: number;
}

/**
 * The partner workspace home: lifetime reach, paying customers, the last twelve
 * months of commission, and the newest ledger entries.
 *
 * `clicks` and `signups` count every link the partner has ever created,
 * including archived ones, because they are a lifetime record rather than a
 * description of the current list.
 *
 * `monthly` is scoped to money accruing to THIS account, matching the balances
 * on the earnings screen: an agency member's rolled-up earnings sit in their
 * owner's account, so they are excluded here exactly as they are excluded from
 * the member's balances. `recent` uses the wider ledger scope instead, so a
 * member still sees the entries they produced.
 */
export interface PartnerOverview {
  clicks: number;
  signups: number;
  /** Distinct referred users whose commission nets above zero. */
  payingCustomers: number;
  /** The last twelve calendar months including the current one, oldest first. */
  monthly: PartnerCommissionMonthPoint[];
  /** The newest ledger entries, same scope and shape as the earnings page. */
  recent: PartnerCommissionEntry[];
}

/**
 * What a partner wants to hear about, as the portal's settings screen reads it.
 *
 * Stored as one object and merged over the server-side defaults on read, so a
 * toggle added later reads as its default for every partner who saved before it
 * existed. None of these is a delivery guarantee: each is a preference the
 * sender consults.
 */
export interface PartnerNotificationPreferences {
  /** A commission was earned or taken back. */
  commission: boolean;
  /** A payout changed state. */
  payouts: boolean;
  /** Program news: rate changes, policy, account standing. */
  program: boolean;
  /** Marketing material and tips. Off by default. */
  resources: boolean;
}

/**
 * Roles inside an agency.
 *
 * `owner` is the partner who created the agency, one per agency, and their row can
 * never be removed or claimed by anybody else. `member` is an invited partner whose
 * commission rolls up to the owner (decision D9, assumption A7). There are no
 * partner-on-partner chains: a member cannot own an agency, so roll-up is exactly
 * one hop.
 */
export const AGENCY_MEMBER_ROLES = ['owner', 'member'] as const;

export type AgencyMemberRole = (typeof AGENCY_MEMBER_ROLES)[number];

/**
 * The lifecycle of one agency membership.
 *
 * `invited` is a minted copy-link invite that nobody has claimed yet, so it has no
 * partner account behind it. `active` is a claimed membership, and the only state
 * that rolls commission up. `removed` is a retirement rather than a deletion:
 * commission entries already labeled with that member keep pointing at a real
 * person, and re-inviting the same email revives the row.
 */
export const AGENCY_MEMBER_STATUSES = ['invited', 'active', 'removed'] as const;

export type AgencyMemberStatus = (typeof AGENCY_MEMBER_STATUSES)[number];

/** An agency, as its owner and its members read it. */
export interface PartnerAgencySummary {
  id: string;
  name: string;
  /** ISO 8601. */
  createdAt: string;
}

/**
 * The caller's own place in an agency.
 *
 * Null for a partner with no agency involvement. `agencyName` is repeated here so
 * a member screen can name the agency without a second call.
 */
export interface PartnerAgencyMembership {
  role: AgencyMemberRole;
  status: AgencyMemberStatus;
  agencyName: string;
}

/**
 * One member row as the owner's members list shows it.
 *
 * `partnerAccountId` and `displayName` are null while an invite is outstanding:
 * there is no partner identity behind an unclaimed invite. Stats are lifetime and
 * scoped to that member's own links and referrals.
 */
export interface PartnerAgencyMemberItem {
  id: string;
  email: string;
  /** The name the owner typed on the invite, if any. */
  name: string | null;
  role: AgencyMemberRole;
  status: AgencyMemberStatus;
  /** ISO 8601. */
  invitedAt: string;
  /** ISO 8601, null until the invite is claimed. */
  joinedAt: string | null;
  /** ISO 8601, null unless the member was removed. */
  removedAt: string | null;
  /** PartnerAccount.id once the invite is claimed, null while it is outstanding. */
  partnerAccountId: string | null;
  /** The member's partner display name, from their PartnerAccount. */
  displayName: string | null;
  stats: PartnerAgencyMemberStats;
}

/**
 * Lifetime totals for one agency member.
 *
 * `earnedMinorByCurrency` is net of reversals and holds what this member earned
 * FOR the agency, so it is empty for an owner: an owner's own referrals accrue
 * directly and are never labeled with an earner. Currencies are never combined;
 * there is no exchange rate anywhere in this system.
 */
export interface PartnerAgencyMemberStats {
  clicks: number;
  signups: number;
  earnedMinorByCurrency: Array<{
    /** ISO 4217, uppercase. */
    currency: string;
    amountMinor: number;
  }>;
}

/**
 * The lifecycle of one payout. Payouts are manual (assumption A1): every
 * transition after the request is a human action recorded by an admin.
 *
 * `requested` and `approved` are the two OPEN states, and a partner may have at
 * most one open payout per currency: the commission entries a payout has claimed
 * are stamped with its id and are out of the payable balance until it resolves.
 *
 * `paid` is terminal and immutable. It is the only state that moves the claimed
 * ledger entries to `paid`, and nothing reopens it: a paid entry stays paid, so a
 * later refund is a new negative ledger entry against future balance rather than
 * an edit to a payout.
 *
 * `rejected` releases the claimed entries back to the payable balance. The payout
 * row is kept rather than deleted so a refusal stays auditable.
 */
export const PARTNER_PAYOUT_STATUSES = [
  'requested',
  'approved',
  'paid',
  'rejected',
] as const;

export type PartnerPayoutStatus = (typeof PARTNER_PAYOUT_STATUSES)[number];

/**
 * One payout, as the portal lists it and the admin queue shows it.
 *
 * `amountMinor` is the sum of the commission entries this payout claimed, in
 * minor units of `currency`, net of reversals. It is decided server side from the
 * ledger: a partner never names their own payout amount.
 *
 * `reference` is the transfer id an admin records when marking the payout paid,
 * which is the evidence the money actually moved. `notes` is the admin's own note,
 * for example why a payout was rejected; neither field ever carries payout method
 * details.
 */
export interface PartnerPayoutSummary {
  id: string;
  amountMinor: number;
  /** ISO 4217, uppercase. */
  currency: string;
  status: PartnerPayoutStatus;
  /** ISO 8601. */
  requestedAt: string;
  /** ISO 8601, null until an admin approves. */
  approvedAt: string | null;
  /** ISO 8601, null until an admin records the transfer. */
  paidAt: string | null;
  /** ISO 8601, null unless the payout was rejected. */
  rejectedAt: string | null;
  /** The transfer reference, set only once the payout is paid. */
  reference: string | null;
  notes: string | null;
}

/**
 * How a partner wants to be paid (open item O2).
 *
 * Deliberately three coarse options rather than a per-corridor taxonomy: payouts
 * are manual, so the value of this field is telling a human which rail to use.
 * `other` exists so a partner in a market neither rail serves can describe their
 * arrangement in `notes` instead of picking a wrong option.
 */
export const PARTNER_PAYOUT_METHODS = ['bank', 'paypal', 'other'] as const;

export type PartnerPayoutMethod = (typeof PARTNER_PAYOUT_METHODS)[number];

/**
 * A partner's payout method details. Sensitive: stored server side, never
 * logged, never exported, and only ever read back by the partner who owns them
 * or by an admin holding `payouts.read`.
 *
 * Every field except `method` is optional, because which ones matter depends on
 * the rail and because a partner filling this in over two sittings must not be
 * forced to invent values. Missing fields read as null rather than being absent,
 * so a form can bind to the shape directly.
 */
export interface PartnerPayoutDetails {
  method: PartnerPayoutMethod;
  /** The name on the receiving account. */
  accountName: string | null;
  bankName: string | null;
  iban: string | null;
  paypalEmail: string | null;
  /** Anything the rails above cannot express, for the human making the transfer. */
  notes: string | null;
}

/**
 * Where a partner's identity details stand (D64). Self-declared and reviewed by
 * hand: `submitted` is waiting for an admin, `verified` clears payouts for
 * approval, and `needs_changes` carries the admin's note and blocks requests
 * until the partner edits the details (which puts them back to `submitted`).
 */
export const PARTNER_KYC_STATUSES = [
  'submitted',
  'verified',
  'needs_changes',
] as const;

export type PartnerKycStatus = (typeof PARTNER_KYC_STATUSES)[number];

/**
 * A partner's identity details, as they submit them. Sensitive: sealed at rest
 * (AES-256-GCM), never logged, and only read back in full by an admin holding
 * `payouts.read`. The partner's own read view masks the tax id and date of birth.
 */
export interface PartnerKycDetails {
  legalName: string;
  /** YYYY-MM-DD. */
  dateOfBirth: string;
  /** ISO 3166-1 alpha-2, uppercase. */
  countryCode: string;
  addressLine1: string;
  addressLine2: string | null;
  city: string;
  region: string | null;
  postalCode: string | null;
  taxId: string | null;
}

/** Details with the two most sensitive fields masked for the partner's read view. */
export interface PartnerKycMaskedDetails
  extends Omit<PartnerKycDetails, 'dateOfBirth' | 'taxId'> {
  /** Only the year, e.g. "1990". */
  birthYear: string;
  /** Last four characters behind bullets, or null when none was given. */
  taxIdMasked: string | null;
}

/** What the partner reads back: the review state plus their masked details. */
export interface PartnerKycView {
  status: PartnerKycStatus;
  /** The admin's note, set only when `status` is `needs_changes`. */
  reviewNote: string | null;
  /** ISO 8601. */
  submittedAt: string;
  reviewedAt: string | null;
  details: PartnerKycMaskedDetails;
}

/** An admin's full view of a partner's identity details, for the payout review. */
export interface PartnerKycAdminView {
  status: PartnerKycStatus;
  reviewNote: string | null;
  submittedAt: string;
  reviewedAt: string | null;
  details: PartnerKycDetails;
}
